<template>
  <v-layout row>
    <v-flex xs12 sm6 offset-sm3>
      <v-text-field :label="label" :loading="loadingUsers" :disabled="loadingUsers" prepend-inner-icon="search" v-model="filterValue" clearable placeholder="Search for Name, Email or Username" :hint="hint" persistent-hint v-on:input="filterChanged" class="mx-2 mx-sm-0 mb-3">
        <template v-slot:append-outer>
          <v-tooltip top open-delay="1000">
            <template v-slot:activator="{ on: tooltip }">
              <v-btn v-on={...tooltip} @click="refreshUsers" :loading="loadingUsers" class="ma-0 pa-0" min-width="32"><v-icon>refresh</v-icon>
                <template v-slot:loader><v-progress-circular indeterminate size="17" width="1.5"></v-progress-circular></template>
              </v-btn>
            </template>
            <span>Refresh all User data.</span>
          </v-tooltip>
        </template>
      </v-text-field>
      <v-alert dismissible outlined v-model="errorLoadingUsers" type="error">Unable to load the Users at this time.</v-alert>
      <v-list v-show="showFilteredAndSortedUsers" light subheader three-line>
        <div class="text-left" v-for="user in filteredAndSortedUsers" :key="user.id">
          <v-list-item>
            <v-list-item-content>
              <v-list-item-title>{{user.firstName}} {{user.lastName}}</v-list-item-title>
              <v-list-item-subtitle v-html="user.username"></v-list-item-subtitle>
              <v-list-item-subtitle v-html="user.email"></v-list-item-subtitle>
            </v-list-item-content>
            <v-list-item-action>
              <action-menu>
                <view-user-action v-if="permissions.allowViewingOfAnyUser" :user="user" :displayViewUserDialog="displayViewUserDialog"></view-user-action>
                <edit-user-action v-if="permissions.allowEditingOfAnyUser" :user="user"></edit-user-action>
                <manage-user-action v-if="permissions.allowEditingOfAnyUser" :user="user"></manage-user-action>
                <edit-password-action v-if="permissions.allowEditingOfAnyUser" :user="user" :displayEditPasswordDialog="displayEditPasswordDialog"></edit-password-action>
                <edit-username-action v-if="permissions.allowEditingOfAnyUser" :user="user" :displayEditUsernameDialog="displayEditUsernameDialog"></edit-username-action>
              </action-menu>
            </v-list-item-action>
          </v-list-item>
          <v-divider light/>
        </div>
      </v-list>
      <view-user-dialog v-model="displayViewUser" :user="currentUser"></view-user-dialog>
      <edit-password-dialog v-model="displayEditPassword" :user="currentUser" :changePassword="changePassword"></edit-password-dialog>
      <edit-username-dialog v-model="displayEditUsername" :user="currentUser" :changeUsername="changeUsername"></edit-username-dialog>
    </v-flex>
  </v-layout>
</template>

<script>
//
// See Note 2 in 'doc/Notes.txt' for information pertaining to this component.
//
import Vue from 'vue';
import Component from 'vue-class-component';
import { Prop } from 'vue-property-decorator';
import { debounce } from 'debounce';
import ActionMenu from '@/components/action/ActionMenu.vue';
import ViewUserAction from '@/components/user/ViewUserAction.vue';
import EditUserAction from '@/components/user/EditUserAction.vue';
import EditPasswordAction from '@/components/user/EditPasswordAction.vue';
import EditPasswordDialog from '@/components/user/EditPasswordDialog.vue';
import EditUsernameAction from '@/components/user/EditUsernameAction.vue';
import EditUsernameDialog from '@/components/user/EditUsernameDialog.vue';
import ManageUserAction from '@/components/user/ManageUserAction.vue';
import ViewUserDialog from '@/components/user/ViewUserDialog.vue';
import userService from '@/services/User';
import userComparator from '@/comparators/User';
import userFilter from '@/filters/User';

@Component({
  components: {
    ActionMenu,
    EditPasswordAction,
    EditPasswordDialog,
    EditUserAction,
    EditUsernameAction,
    EditUsernameDialog,
    ManageUserAction,
    ViewUserAction,
    ViewUserDialog,
  },
})
export default class SearchUsers extends Vue {
  @Prop(Function) changePassword;
  @Prop(Function) changeUsername;
  @Prop(Object) permissions;

  loadingUsers = true;
  errorLoadingUsers = false;
  filteringUsers = false;
  filterValue = '';
  debouncedFilterValue = '';
  sortedUsers = [];
  filteredAndSortedUsers = [];
  minimumCharacters = 3;
  currentUser = {};
  displayViewUser = false;
  displayEditPassword = false;
  displayEditUsername = false;

  get showFilteredAndSortedUsers() {
    return this.filteredAndSortedUsers.length > 0;
  }

  get label() {
    if (this.filteringUsers) {
      return 'Searching ...';
    }
    return '';
  }

  get hint() {
    if (this.filterIsSearchable) {
      const count = this.filteredAndSortedUsers.length;
      const text = count === 1 ? 'user found' : 'users found';
      return `${count} ${text}`;
    }
    return `Enter at least ${this.minimumCharacters} characters to search`;
  }

  get filterIsSearchable() {
    return this.debouncedFilterValue !== null && this.debouncedFilterValue.length >= this.minimumCharacters;
  }

  getFilteredUsers() {
    return this.filterIsSearchable ? this.sortedUsers.filter(userFilter.byNameEmailUsername(this.debouncedFilterValue)) : [];
  }

  setFilteredAndSortedUsers(/* filterValue */) {
    this.filteredAndSortedUsers = this.getFilteredUsers();
    this.filteringUsers = false;
  }

  debouncedSetFilteredAndSortedUsers = debounce(this.setFilteredAndSortedUsers, 200);

  filterChanged(filterValue) {
    this.debouncedFilterValue = filterValue;
    if (this.filterIsSearchable) {
      this.filteringUsers = true;
    }
    this.debouncedSetFilteredAndSortedUsers(filterValue);
  }

  refreshUsers() {
    this.loadingUsers = true;
    this.errorLoadingUsers = false;
    userService.getAll().then((users) => {
      this.loadingUsers = false;
      this.sortedUsers = users.sort(userComparator.byFirstNameThenLastName);
      this.filteredAndSortedUsers = this.getFilteredUsers();
    }).catch((error) => {
      console.error('Error getting users:', error.message);
      this.errorLoadingUsers = true;
      this.loadingUsers = false;
      this.sortedUsers = [];
      this.filteredAndSortedUsers = [];
    });
  }

  created() {
    this.refreshUsers();
  }

  displayViewUserDialog(user) {
    this.currentUser = user;
    this.displayViewUser = true;
  }

  displayEditPasswordDialog(user) {
    this.currentUser = user;
    this.displayEditPassword = true;
  }

  displayEditUsernameDialog(user) {
    this.currentUser = user;
    this.displayEditUsername = true;
  }
}
</script>
